
import React from 'react';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, Bar, Cell, AreaChart, Area, RadarChart as RChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, ScatterChart, Scatter, Label, PieChart, Pie, LabelList, ReferenceArea, ReferenceLine, ZAxis } from 'recharts';
import { ChartConfig, MeasureConfig } from '../types';
import { AdvancedVisuals } from './AdvancedVisuals';
import { HeatmapRenderer } from './HeatmapRenderer';
import { KPICard } from './KPICard/KPICard';
import { COLOR_PALETTES, DEFAULT_PALETTE, CHART_COLORS } from '../constants';
import { resolveSeriesColors } from '../utils/resolveSeriesColors';
import { cleanLabel } from '../utils/stringUtils';

const CustomBarLabel: React.FC<any> = (props) => {
    const { x, y, width, value, payload } = props;
    const displayValue = payload?._label || value;
    return (
        <text
            x={x + width / 2}
            y={y}
            dy={-10}
            fill="#ffffff"
            fontSize={10}
            fontWeight="700"
            textAnchor="middle"
            style={{ textShadow: '0 1px 2px rgba(255,255,255,0.85)' }}
        >
            {typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}
        </text>
    );
};

// Custom Legend Component
const CustomLegend: React.FC<{
    items: { id: string; label: string; color: string; visible: boolean }[];
    onToggle: (id: string) => void;
    className?: string;
}> = ({ items, onToggle, className = "" }) => {
    if (!items || items.length === 0) return null;

    return (
        <div className={`flex flex-col gap-1 ${className}`}>
            {items.map((item) => (
                <div
                    key={item.id}
                    onClick={() => onToggle(item.id)}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors hover:bg-white/10 ${!item.visible ? 'opacity-50 grayscale' : ''}`}
                >
                    <div
                        className="w-2.5 h-2.5 rounded-full shadow-sm flex-shrink-0"
                        style={{ backgroundColor: item.color, boxShadow: item.visible ? `0 0 6px ${item.color}` : 'none' }}
                    />
                    <span className="text-xs font-medium text-white break-words" title={item.label}>
                        {item.label}
                    </span>
                </div>
            ))}
        </div>
    );
};

import { HighlightState } from '../contexts/InteractionContext';

interface ChartRendererProps {
    config: ChartConfig;
    data: any[];
    isPreview?: boolean;
    isVisible?: boolean;
    canvasRef?: React.MutableRefObject<any>;
    onSelectionChange?: (selection: any) => void;
    // Cross-chart interaction props
    chartId?: string;
    highlightState?: HighlightState | null;
    onDataPointClick?: (field: string, value: any) => void;
    onDataPointHover?: (field: string, value: any) => void;
    onMouseLeave?: () => void;
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({
    config,
    data,
    isPreview = false,
    isVisible = true,
    canvasRef,
    onSelectionChange,
    chartId,
    highlightState,
    onDataPointClick,
    onDataPointHover,
    onMouseLeave
}) => {
    const isAdvanced = ['scientific', 'vector', '3d', 'globe', 'force-directed', '3d-bar', 'map'].includes(config.type);
    const { customization, xField, yField } = config;

    // Local state for toggling series visibility
    const [hiddenSeries, _setHiddenSeries] = React.useState<Set<string>>(new Set());
    const setHiddenSeries = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setHiddenSeries called with second argument!', args);
            console.trace();
        }
        return _setHiddenSeries(args[0]);
    };

    const toggleSeries = (id: string) => {
        const newHidden = new Set(hiddenSeries);
        if (newHidden.has(id)) {
            newHidden.delete(id);
        } else {
            newHidden.add(id);
        }
        setHiddenSeries(newHidden);
    };

    const textColor = '#ffffff'; // White for all charts
    const gridColor = isAdvanced ? '#4A5568' : '#e2e8f0'; // gray-600 for dark, gray-200 for light
    const chartColor = customization?.color || '#4f46e5';

    // Resolve Colors (Moved up for Heatmap access)
    const currentPalette = config.customization?.paletteName === 'custom' && config.customization?.customPalette
        ? config.customization.customPalette
        : (COLOR_PALETTES[config.customization?.paletteName || DEFAULT_PALETTE] || CHART_COLORS);

    // KPI Card - render before advanced charts check
    if (config.type === 'kpi-card') {
        return (
            <KPICard
                config={config}
                data={data}
                onCardClick={onDataPointClick ? (groupValue) => {
                    if (groupValue && config.kpiGroupByField) {
                        onDataPointClick(config.kpiGroupByField, groupValue);
                    }
                } : undefined}
            />
        );
    }

    if (isAdvanced) {
        return <AdvancedVisuals config={config} data={data} isVisible={isVisible} canvasRef={canvasRef} />;
    }



    if (!data || data.length === 0) return <div className="flex items-center justify-center h-full text-gray-500">Configure your chart to see a preview.</div>;

    // Heatmap Rendering - Use custom dimension tracking
    // Heatmap Rendering
    if (config.type === 'heatmap') {
        return <ResponsiveHeatmapContainer data={data} config={config} palette={currentPalette} />;
    }

    // Data check moved up


    if (config.type === 'number') {
        const value = data[0]?.value;
        // Use measure color if available, otherwise fall back to chartColor
        const numberMeasure = config.measures?.find(m => m.visible);
        const numberColor = numberMeasure?.colorMode === 'custom' && numberMeasure?.color
            ? numberMeasure.color
            : (currentPalette[0] || customization?.color || '#f59e0b');
        return (
            <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="text-6xl font-bold" style={{ color: numberColor }}>{typeof value === 'number' ? value.toLocaleString() : 'N/A'}</div>
                <div className="text-lg text-gray-500 mt-2">{config.title}</div>
            </div>
        );
    }

    // Determine active measures (normalize legacy ySeries if needed)
    let activeMeasures: MeasureConfig[] = config.measures?.filter(m => m.visible) || [];

    // Legacy Fallback: Map ySeries to MeasureConfig
    if (activeMeasures.length === 0 && config.ySeries) {
        activeMeasures = config.ySeries.filter(s => s.visible).map(s => ({
            id: s.field, // Use field as ID for legacy compatibility with dataService
            field: s.field,
            aggregation: s.aggregation as any,
            displayName: s.label || s.field,
            isCustomLabel: !!s.label,
            colorMode: s.color && s.color !== 'auto' ? 'custom' : 'auto',
            color: s.color !== 'auto' ? s.color : undefined,
            visible: true
        }));
    }

    // Legacy Fallback: Map yField to MeasureConfig
    if (activeMeasures.length === 0 && yField) {
        activeMeasures.push({
            id: 'legacy_y',
            field: yField,
            aggregation: config.aggregation as any || 'sum',
            displayName: config.title || 'Value',
            isCustomLabel: false,
            colorMode: 'auto',
            visible: true
        });
    }

    // Resolve Colors
    // currentPalette is now defined at the top

    // Use ALL measures for color resolution to maintain stability across visibility toggles
    const allMeasures = config.measures || activeMeasures;
    const colorMap = resolveSeriesColors(allMeasures, currentPalette);

    const renderSingleChart = (chartData: any[], facetTitle?: string) => {
        // Helper to generate series based on encoding
        const getEncodedSeries = () => {
            // Case 0: Scatter Chart (Multi-Series)
            if (config.type === 'scatter') {
                // chartData is already an array of series objects from dataService
                // Check if color encoding is active (series have colorCategory)
                const hasColorEncoding = chartData.some((s: any) => s.colorCategory !== undefined);

                return chartData.map((s: any, i: number) => {
                    // For color encoding, use groupIndex to get consistent palette color
                    // For detail encoding (no colorCategory), use measure color
                    let color;
                    if (hasColorEncoding && s.groupIndex !== undefined) {
                        color = currentPalette[s.groupIndex % currentPalette.length];
                    } else {
                        color = colorMap.get(s.measureId) || currentPalette[i % currentPalette.length];
                    }

                    return {
                        dataKey: s.seriesId,
                        name: s.label,
                        color,
                        measureId: s.measureId,
                        seriesId: s.seriesId,
                        points: s.points
                    };
                });
            }

            // Case 1: Color Encoding Active (Breakdown)
            if (config.encoding?.color?.field && chartData.length > 0) {
                const encodedSeries: any[] = [];
                const colorCategories = new Set<string>();

                // Scan data to find categories (keys like "measureId::category")
                chartData.forEach(row => {
                    Object.keys(row).forEach(key => {
                        if (key.includes('::')) {
                            const [measureId, category] = key.split('::');
                            // Check if this key belongs to an active measure
                            if (activeMeasures.some(m => m.id === measureId || m.field === measureId)) {
                                colorCategories.add(category);
                            }
                        }
                    });
                });

                const sortedCategories = Array.from(colorCategories).sort();

                activeMeasures.forEach((measure) => {
                    sortedCategories.forEach((category, cIdx) => {
                        // Assign colors cyclically for categories
                        const color = currentPalette[cIdx % currentPalette.length];
                        const seriesId = `${measure.id}::${category}`;

                        // Generate clean label
                        let label = category;
                        if (activeMeasures.length > 1) {
                            label = `${measure.displayName || cleanLabel(measure.field)} - ${category}`;
                        }

                        encodedSeries.push({
                            dataKey: seriesId,
                            name: label,
                            color: color,
                            measureId: measure.id,
                            seriesId: seriesId // Unique ID for visibility toggle
                        });
                    });
                });

                return encodedSeries;
            }

            // Case 2: Detail Encoding Active (Breakdown with same color)
            if (config.encoding?.detail?.field && chartData.length > 0) {
                const encodedSeries: any[] = [];
                const detailCategories = new Set<string>();

                // Scan data to find detail categories (keys like "measureId@@category")
                chartData.forEach(row => {
                    Object.keys(row).forEach(key => {
                        if (key.includes('@@')) {
                            const [measureId, category] = key.split('@@');
                            // Check if this key belongs to an active measure
                            if (activeMeasures.some(m => m.id === measureId || m.field === measureId)) {
                                detailCategories.add(category);
                            }
                        }
                    });
                });

                const sortedCategories = Array.from(detailCategories).sort();

                activeMeasures.forEach((measure, mIdx) => {
                    // Use same color for all detail groups within a measure
                    const measureColor = colorMap.get(measure.id) || currentPalette[mIdx % currentPalette.length];

                    sortedCategories.forEach((category) => {
                        const seriesId = `${measure.id}@@${category}`;

                        // Generate clean label
                        let label = category;
                        if (activeMeasures.length > 1) {
                            label = `${measure.displayName || cleanLabel(measure.field)} - ${category}`;
                        }

                        encodedSeries.push({
                            dataKey: seriesId,
                            name: label,
                            color: measureColor, // Same color for all detail groups
                            measureId: measure.id,
                            seriesId: seriesId
                        });
                    });
                });

                return encodedSeries;
            }

            // Case 3: No Color or Detail Encoding (Standard Multi-Measure)
            return activeMeasures.map((measure) => {
                // Generate clean label if displayName is empty/default
                const label = measure.displayName || cleanLabel(`${measure.aggregation} of ${measure.field}`);

                return {
                    dataKey: measure.id,
                    name: label,
                    color: colorMap.get(measure.id) || '#ccc',
                    measureId: measure.id,
                    seriesId: measure.id
                };
            });
        };

        const allSeries = getEncodedSeries();
        // Filter series for rendering based on hidden state
        const visibleSeries = allSeries.filter(s => !hiddenSeries.has(s.seriesId));

        // Prepare Legend Items
        const legendItems = allSeries.map(s => ({
            id: s.seriesId,
            label: s.name,
            color: s.color,
            visible: !hiddenSeries.has(s.seriesId)
        }));

        // Determine legend items for Pie chart specifically (since it differs from series-based charts)
        let finalLegendItems = legendItems;
        if (config.type === 'pie') {
            finalLegendItems = chartData.map((entry, index) => ({
                id: entry.name,
                label: entry.name,
                color: currentPalette[index % currentPalette.length],
                visible: !hiddenSeries.has(entry.name)
            }));
        }

        // Layout Logic: Side-by-Side Legend
        const showLegend = customization?.showLegend && finalLegendItems.length > 0;
        const legendWidth = 240;
        const rightMargin = 10;

        const commonProps = {
            margin: { top: 15, right: rightMargin, left: 10, bottom: 20 },
            onMouseLeave: onMouseLeave
        };

        const AxisGroup = () => (
            <>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={customization?.showGrid === false ? 'transparent' : gridColor} />
                <XAxis dataKey="name" tick={{ fill: textColor, fontSize: 11 }} stroke={textColor} tickLine={{ stroke: textColor }}>
                    {customization?.xAxisLabel && <Label value={customization.xAxisLabel} offset={-15} position="insideBottom" fill={textColor} />}
                </XAxis>
                <YAxis tick={{ fill: textColor, fontSize: 11 }} stroke={textColor} tickLine={{ stroke: textColor }}>
                    {customization?.yAxisLabel && <Label value={customization.yAxisLabel} angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fill: textColor }} />}
                </YAxis>
                <RechartsTooltip
                    contentStyle={{
                        background: 'rgba(2, 4, 8, 0.9)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '0.5rem',
                        padding: '8px 12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        color: '#fff',
                        backdropFilter: 'blur(8px)'
                    }}
                    cursor={{ fill: 'rgba(245, 158, 11, 0.1)' }}
                />
            </>
        );

        const renderChartContent = () => {
            switch (config.type) {
                case 'bar':
                    // Calculate opacity for highlighting
                    const getBarOpacity = (entry: any) => {
                        if (!highlightState || highlightState.sourceChartId === chartId) return 1;
                        const value = entry.name ?? entry[highlightState.field];
                        return highlightState.values.includes(value) ? 1 : 0.2;
                    };
                    return (
                        <BarChart data={chartData} {...commonProps}>
                            <AxisGroup />
                            {visibleSeries.map((s, i) => (
                                <Bar
                                    key={s.dataKey}
                                    dataKey={s.dataKey}
                                    name={s.name}
                                    fill={s.color}
                                    radius={config.customization?.roundedBars ? [4, 4, 0, 0] : 0}
                                    label={customization?.showDataLabels ? <CustomBarLabel /> : undefined}
                                    stackId={config.customization?.stacked ? "stack" : undefined}
                                    onClick={(data: any) => {
                                        if (onDataPointClick && data?.name) {
                                            onDataPointClick('name', data.name);
                                        }
                                    }}
                                    onMouseEnter={(data: any) => {
                                        if (onDataPointHover && data?.name) {
                                            onDataPointHover('name', data.name);
                                        }
                                    }}
                                    style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={(!config.encoding?.color?.field && activeMeasures.length === 1 && customization?.useMultipleColors)
                                                ? currentPalette[index % currentPalette.length]
                                                : s.color
                                            }
                                            opacity={getBarOpacity(entry)}
                                        />
                                    ))}
                                </Bar>
                            ))}
                        </BarChart>
                    );
                case 'line':
                    // Calculate opacity for line highlighting
                    const getLineOpacity = (entry: any) => {
                        if (!highlightState || highlightState.sourceChartId === chartId) return 1;
                        const value = entry.name ?? entry[highlightState.field];
                        return highlightState.values.includes(value) ? 1 : 0.2;
                    };
                    return (
                        <LineChart data={chartData} {...commonProps}>
                            <AxisGroup />
                            {visibleSeries.map((s, i) => (
                                <Line
                                    key={s.dataKey}
                                    type="monotone"
                                    dataKey={s.dataKey}
                                    name={s.name}
                                    stroke={s.color}
                                    strokeWidth={config.customization?.lineWidth || 2}
                                    dot={({ cx, cy, payload, index }) => {
                                        const opacity = getLineOpacity(payload);
                                        return (
                                            <circle
                                                key={index}
                                                cx={cx}
                                                cy={cy}
                                                r={3}
                                                fill={s.color}
                                                opacity={opacity}
                                                style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                                                onMouseEnter={() => onDataPointHover && onDataPointHover('name', payload.name)}
                                                onClick={() => onDataPointClick && onDataPointClick('name', payload.name)}
                                            />
                                        );
                                    }}
                                    activeDot={{ r: 5 }}
                                    connectNulls={true}
                                >
                                    {customization?.showDataLabels && <LabelList dataKey={config.encoding?.label?.field ? "_label" : s.dataKey} position="top" style={{ fill: 'white', fontSize: 10 }} />}
                                </Line>
                            ))}
                        </LineChart>
                    );
                case 'area':
                    return (
                        <AreaChart data={chartData} {...commonProps}>
                            <defs>
                                {visibleSeries.map((s, i) => {
                                    const areaOpacity = config.customization?.areaOpacity ?? 0.8;
                                    return (
                                        <linearGradient key={s.dataKey} id={`color-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={s.color} stopOpacity={areaOpacity} />
                                            <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                                        </linearGradient>
                                    );
                                })}
                            </defs>
                            <AxisGroup />
                            {visibleSeries.map((s, i) => (
                                <Area
                                    key={s.dataKey}
                                    type="monotone"
                                    dataKey={s.dataKey}
                                    name={s.name}
                                    stroke={s.color}
                                    fillOpacity={1}
                                    fill={`url(#color-${s.dataKey})`}
                                    connectNulls={true}
                                    stackId={config.customization?.stacked ? "stack" : undefined}
                                />
                            ))}
                        </AreaChart>
                    );
                case 'scatter':
                    // Generate a unique key based on measure aggregations to force re-render
                    const scatterKey = visibleSeries.map(s => `${s.seriesId}-${activeMeasures.find(m => m.id === s.measureId)?.aggregation || 'default'}`).join('_');

                    return (
                        <ScatterChart key={scatterKey} margin={{ top: 15, right: rightMargin, left: 10, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={customization?.showGrid === false ? 'transparent' : gridColor} />
                            <XAxis type="number" dataKey="x" name={xField?.split('.')[1]} tick={{ fill: textColor, fontSize: 11 }} stroke={textColor} tickLine={{ stroke: textColor }}>
                                {customization?.xAxisLabel && <Label value={customization.xAxisLabel} offset={-15} position="insideBottom" fill={textColor} />}
                            </XAxis>
                            <YAxis type="number" dataKey="y" name={yField?.split('.')[1]} tick={{ fill: textColor, fontSize: 11 }} stroke={textColor} tickLine={{ stroke: textColor }}>
                                {customization?.yAxisLabel && <Label value={customization.yAxisLabel} angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fill: textColor }} />}
                            </YAxis>
                            {/* ZAxis for Size Encoding */}
                            {config.sizeField && <ZAxis type="number" dataKey="size" range={[50, 400]} name={config.sizeField} />}

                            <RechartsTooltip
                                cursor={{ strokeDasharray: '3 3' }}
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div className="bg-slate-900 border border-slate-700 p-2 rounded shadow-lg text-xs text-white">
                                                <div className="font-bold mb-1">{data.label || data.name || 'Point'}</div>
                                                <div>X: {data.x?.toLocaleString()}</div>
                                                <div>Y: {data.y?.toLocaleString()}</div>
                                                {data.size !== undefined && <div>Size: {data.size?.toLocaleString()}</div>}
                                                {data.seriesId !== 'default' && <div>Series: {data.seriesId}</div>}
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            {visibleSeries.map((s) => (
                                <Scatter
                                    key={s.seriesId}
                                    name={s.name}
                                    data={s.points} // Use points from series object
                                    fill={s.color}
                                    shape="circle"
                                />
                            ))}
                        </ScatterChart>
                    );
                case 'pie':
                    const pieValKey = activeMeasures[0]?.id || 'value';
                    const total = chartData.reduce((sum, entry) => sum + (entry[pieValKey] || 0), 0);

                    // Filter data for Pie
                    const visiblePieData = chartData.filter(d => !hiddenSeries.has(d.name));

                    // Calculate opacity for pie highlighting
                    const getPieOpacity = (entry: any) => {
                        if (!highlightState || highlightState.sourceChartId === chartId) return 1;
                        const value = entry.name ?? entry[highlightState.field];
                        return highlightState.values.includes(value) ? 1 : 0.2;
                    };

                    return (
                        <>
                            <PieChart>
                                <Pie
                                    data={visiblePieData}
                                    dataKey={pieValKey}
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius="70%"
                                    fill="#8884d8"
                                    label={customization?.showDataLabels ? (entry) => `${entry.name} (${((entry[pieValKey] / total) * 100).toFixed(0)}%)` : undefined}
                                    onClick={(data: any) => {
                                        if (onDataPointClick && data?.name) {
                                            onDataPointClick('name', data.name);
                                        }
                                    }}
                                    onMouseEnter={(data: any) => {
                                        if (onDataPointHover && data?.name) {
                                            onDataPointHover('name', data.name);
                                        }
                                    }}
                                    style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                                >
                                    {visiblePieData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={currentPalette[index % currentPalette.length]}
                                            opacity={getPieOpacity(entry)}
                                        />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </>
                    );
                case 'heatmap':
                    return <ResponsiveHeatmapContainer data={chartData} config={config} palette={currentPalette} />;
                case 'radar':
                    return (
                        <RChart data={chartData} outerRadius="80%">
                            <PolarGrid stroke={gridColor} />
                            <PolarAngleAxis dataKey="name" tick={{ fill: textColor, fontSize: 11 }} />
                            <PolarRadiusAxis stroke={textColor} tick={{ fill: textColor, fontSize: 10 }} />
                            {visibleSeries.map((m, i) => (
                                <Radar
                                    key={m.measureId}
                                    name={m.name}
                                    dataKey={m.measureId}
                                    stroke={m.color}
                                    fill={m.color}
                                    fillOpacity={0.4}
                                />
                            ))}
                        </RChart>
                    );
                default:
                    return null;
            }
        };

        const legendPosition = customization?.legendPosition || 'right';
        const isOverlayLegend = legendPosition.includes('-'); // top-left, etc.
        const isVerticalLayout = legendPosition === 'top' || legendPosition === 'bottom';

        const LegendPanel = () => (
            <div
                className={`flex-shrink-0 flex flex-col bg-black/20 overflow-y-auto custom-scrollbar p-3 backdrop-blur-sm ${isOverlayLegend ? 'absolute z-20 rounded-lg border border-white/10 shadow-lg max-h-[50%]' :
                    isVerticalLayout ? 'w-full h-auto max-h-[30%] border-white/10' :
                        'h-full border-white/10 w-[240px]'
                    } ${legendPosition === 'top' ? 'border-b' :
                        legendPosition === 'bottom' ? 'border-t' :
                            legendPosition === 'left' ? 'border-r' :
                                legendPosition === 'right' ? 'border-l' : ''
                    } ${legendPosition === 'top-left' ? 'top-2 left-2' :
                        legendPosition === 'top-right' ? 'top-2 right-2' :
                            legendPosition === 'bottom-left' ? 'bottom-2 left-2' :
                                legendPosition === 'bottom-right' ? 'bottom-2 right-2' : ''
                    }`}
                style={isOverlayLegend ? { maxWidth: '240px' } : {}}
            >
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">Legend</h4>
                <CustomLegend items={finalLegendItems} onToggle={toggleSeries} />
            </div>
        );

        return (
            <div className={`w-full h-full flex relative ${isVerticalLayout ? 'flex-col' : 'flex-row'}`}>
                {/* Top/Left Legend */}
                {showLegend && (legendPosition === 'top' || legendPosition === 'left') && <LegendPanel />}

                {/* Chart Area */}
                <div className="flex-1 min-w-0 relative h-full overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%">
                        {renderChartContent()}
                    </ResponsiveContainer>
                    {/* Overlays */}
                    {!isFaceted && overlayAnnotations.map(renderAnnotation)}

                    {/* Overlay Legend */}
                    {showLegend && isOverlayLegend && <LegendPanel />}
                </div>

                {/* Bottom/Right Legend */}
                {showLegend && (legendPosition === 'bottom' || legendPosition === 'right') && <LegendPanel />}
            </div>
        );
    };

    const annotations = customization?.annotations || [];
    const topAnnotations = annotations.filter(a => a.position === 'top');
    const bottomAnnotations = annotations.filter(a => a.position === 'bottom');
    const overlayAnnotations = annotations.filter(a => a.position === 'overlay');

    const renderAnnotation = (ann: any) => (
        <div
            key={ann.id}
            style={{
                fontSize: ann.style.fontSize === 'small' ? '12px' : ann.style.fontSize === 'large' ? '20px' : ann.style.fontSize === 'huge' ? '32px' : '16px',
                color: ann.style.color,
                fontWeight: ann.style.isBold ? 'bold' : 'normal',
                fontStyle: ann.style.isItalic ? 'italic' : 'normal',
                textAlign: 'center',
                padding: '4px 8px',
                ...ann.position === 'overlay' ? {
                    position: 'absolute',
                    left: `${ann.x ?? 50}%`,
                    top: `${ann.y ?? 50}%`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    borderRadius: '6px',
                    backdropFilter: 'blur(4px)',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)'
                } : {}
            }}
        >
            {ann.text}
        </div>
    );

    // Facet Rendering Logic
    const isFaceted = !!config.facets?.field && Array.isArray(data) && data.length > 0 && 'facet' in data[0];

    const renderContent = () => {
        // Route advanced chart types (scientific, vector, 3d, etc.) to AdvancedVisuals
        if (isAdvanced) {
            return (
                <div className="w-full h-full">
                    <AdvancedVisuals config={config} data={data} isVisible={isVisible} />
                </div>
            );
        }

        if (isFaceted) {
            const facetData = data as { facet: string, data: any[] }[];
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full overflow-y-auto p-2 custom-scrollbar">
                    {facetData.map((item, idx) => (
                        <div key={idx} className="flex flex-col min-h-[250px] border border-white/10 rounded-lg bg-black/20 overflow-hidden">
                            <div className="bg-white/5 px-3 py-1.5 text-xs font-bold text-gray-300 border-b border-white/5 truncate">
                                {config.facets?.field}: {item.facet}
                            </div>
                            <div className="flex-1 min-h-0 relative p-2">
                                {renderSingleChart(item.data, item.facet)}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        return renderSingleChart(data);
    };

    return (
        <div
            key={config.type}
            className="w-full h-full flex flex-col relative"
            style={{
                background: 'transparent',
                border: 'none',
                borderRadius: 12,
                overflow: 'hidden'
            }}
        >
            {/* Top Docked */}
            {topAnnotations.length > 0 && (
                <div className="flex flex-col items-center p-2 bg-black/10">
                    {topAnnotations.map(renderAnnotation)}
                </div>
            )}

            {/* Main Content (Chart + Legend) */}
            <div className="flex-1 min-h-0 relative w-full">
                {renderContent()}
            </div>

            {/* Bottom Docked */}
            {bottomAnnotations.length > 0 && (
                <div className="flex flex-col items-center p-2 bg-black/10">
                    {bottomAnnotations.map(renderAnnotation)}
                </div>
            )}
        </div>
    );
};

// Helper component for Heatmap to handle resizing without violating Hook rules
const ResponsiveHeatmapContainer = ({ data, config, palette }: { data: any[], config: ChartConfig, palette: string[] }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [dimensions, _setDimensions] = React.useState({ width: 0, height: 0 });
    const setDimensions = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setDimensions called with second argument!', args);
            console.trace();
        }
        return _setDimensions(args[0]);
    };

    React.useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({ width, height });
            }
        };

        updateDimensions();
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            {dimensions.width > 0 && dimensions.height > 0 && (
                <HeatmapRenderer
                    data={data}
                    config={config}
                    palette={palette}
                    width={dimensions.width}
                    height={dimensions.height}
                />
            )}
        </div>
    );
};
